import { Component, EventEmitter, Input, OnChanges, Output } from '@angular/core';
import { UntypedFormGroup } from '@angular/forms';
import { takeUntil } from 'rxjs/operators';
import { AppConfig } from 'src/app/configs/app.config';
import { Authority } from 'src/app/constants/authority';
import { DestroyNotifierComponent } from 'src/app/core/component/destroy/destroy-notifier.component';
import { Status } from 'src/app/core/services/http/general/enums/status';
import { UserAdapterService } from 'src/app/core/services/http/user/adapters/user-adapter.service';
import { Student } from 'src/app/core/services/http/user/interfaces/student';
import { User } from 'src/app/core/services/http/user/interfaces/user';
import { UserService } from 'src/app/core/services/http/user/user.service';
import { ObservableWrapperService } from 'src/app/core/services/observable-wrapper/observable-wrapper.service';
import { clearFormControlsValues, getFileContent, stringToBase64 } from 'src/app/core/utils/utils';
import { AutoCompleteItem } from 'src/app/shared/auto-complete/interfaces/autocomplete-item';
import { FormGroupService } from 'src/app/shared/form-error/form-group.service';
import { RosterAdapterService } from 'src/app/core/services/http/roster/adapters/roster-adapter.service';
import { Roster } from 'src/app/core/services/http/roster/interfaces/roster';
import { RosterService } from 'src/app/core/services/http/roster/roster.service';
import { Paging } from '../../../../core/services/http/general/classes/paging';
import { environment } from 'src/environments/environment';
import { RosterSelectActions } from 'src/app/core/services/http/roster/enums/roster-select-actions';
import { EventManagerService } from '../../../../core/services/event-manager/event-manager.service';
import { EventContent } from '../../../../core/services/event-manager/models/event-content';
import { EventType } from '../../../../constants/events-type';

@Component({
    selector: 'app-roster-manual-upload',
    templateUrl: './roster-manual-upload.component.html',
    styleUrls: ['./roster-manual-upload.component.scss'],
    standalone: false
})
export class RosterManualUploadComponent extends DestroyNotifierComponent implements OnChanges {
  @Input() form: UntypedFormGroup | undefined;

  /** WCAG 3.3.1 */
  hasVisibleError(controlName: string): boolean {
    const c = this.form?.get(controlName);
    if (!c) {
      return false;
    }
    return c.errors !== null && (c.dirty === true || c.touched === true);
  }
  @Input() shouldShowFileUploader = true;
  @Input() shouldShowGroupSelection = true;

  @Output() itemChange = new EventEmitter<any>();
  @Output() selectChange = new EventEmitter<AutoCompleteItem[]>();
  @Output() selectedItemChange = new EventEmitter<AutoCompleteItem>();

  shouldShowFileUploaderArea = false;
  shouldShowGroupSelectionArea = false;
  isUploading: boolean | undefined;
  removedItem: AutoCompleteItem | undefined;
  rosterCsvTemplate = environment.DownloadRoutes.rosterCsvTemplate;

  students: AutoCompleteItem[] = [];
  selectedItemsDataFromFile: AutoCompleteItem[] = [];
  groups: AutoCompleteItem[] = [];

  clearFormControlsValues = clearFormControlsValues;
  paging = new Paging({ pageSize: AppConfig.AutoComplete.pageSize });
  rosterSelectActions = RosterSelectActions;

  constructor(
    private formGroupService: FormGroupService,
    private rosterService: RosterService,
    private rosterAdapterService: RosterAdapterService,
    private userService: UserService,
    private userAdapterService: UserAdapterService,
    private observableWrapperService: ObservableWrapperService,
    private eventManager: EventManagerService
  ) {
    super();
  }

  ngOnChanges(): void {
    this.clearFormControlsValues(['items', 'manualFile'], this.form);
  }

  onRemove(autoCompleteItem: AutoCompleteItem): void {
    this.removedItem = autoCompleteItem;
    this.itemChange.emit();
  }

  onSelectChange(items: AutoCompleteItem[]): void {
    this.sortStudents(items);
    this.selectChange.emit(items);
    this.setFormControlValue(items);
  }

  onSearchChange(filter: string): void {
    this.observableWrapperService.unsubscribeWrapper<User[]>(
      this.userService.getAll(undefined, { filter, role: Authority.Student, status: Status.Active }),
      this.destroyNotify,
      students => {
        this.students = this.userAdapterService.userToAutoCompleteItem(students);
      }
    );
  }

  onSelectChangeRoster(item: AutoCompleteItem): void {
    this.selectedItemChange.emit(item);
    this.form?.controls.groupSelect.setValue(item);
  }

  onSearchChangeRoster(searchedItem: string): void {
    this.observableWrapperService.unsubscribeWrapper<Roster[]>(
      this.rosterService.getAll(this.paging, { filter: searchedItem, status: Status.Active }),
      this.destroyNotify,
      rosters => {
        this.groups = this.rosterAdapterService.rosterToAutoCompleteItem(rosters);
      }
    );
  }

  updateGroupSelectActionState(groupAction: RosterSelectActions): void {
    this.form?.controls.groupSelectAction.setValue(groupAction);
  }

  onFileChange(file: File): void {
    if (!this.form) {
      return;
    }

    this.setVisiblityOfFileUploader(true);

    this.form.controls.manualFile.setValue(file);
    this.formGroupService.validateFormControl(this.form.controls.manualFile);
  }

  async onFileUpload(): Promise<void> {
    if (!this.form) {
      return;
    }

    this.setVisiblityOfFileUploader(true);

    this.formGroupService.validateFormControl(this.form.controls.manualFile);

    if (this.form.controls.manualFile.errors === null) {
      const fileContent = await getFileContent(this.form.controls.manualFile.value);

      if (fileContent.length === 0) {
        return;
      }

      this.isUploading = true;

      this.userService
        .createListFromFileBase64(stringToBase64(fileContent))
        .pipe(takeUntil(this.destroyNotify))
        .subscribe(
          students => {
            this.onSuccess(students);
          },
          () => {
            this.onError();
          }
        );
    }
  }

  async onGroupSelectAction(): Promise<void> {
    if (!this.form) {
      return;
    }

    this.formGroupService.validateFormControl(this.form.controls.groupSelectAction);

    if (this.form.controls.groupSelectAction.errors === null) {
      // TODO DEV: Apply add/remove action on student list from the selected group
      const groupSelectItem: AutoCompleteItem = this.form?.controls.groupSelect.value;
      this.loadGroupSelectDataByUuid(groupSelectItem.value);
    }
  }

  onSuccess(students: Student[]): void {
    this.loadStudents(students);
  }

  onError(): void {
    this.isUploading = false;
  }

  setVisiblityOfFileUploader(visible: boolean): void {
    this.shouldShowFileUploaderArea = visible;
    if (this.shouldShowGroupSelectionArea) {
      this.shouldShowGroupSelectionArea = false;
    }
  }
  setVisiblityOfGroupSelection(visible: boolean): void {
    this.shouldShowGroupSelectionArea = visible;
    if (this.shouldShowFileUploaderArea) {
      this.shouldShowFileUploaderArea = false;
    }
  }

  private setFormControlValue(items: AutoCompleteItem[]): void {
    this.form?.controls.items.setValue(items);
  }

  private loadStudents(students: Student[]): void {
    const items: AutoCompleteItem[] = this.userAdapterService.userToAutoCompleteItem(students);

    this.sortStudents(items);
    this.selectChange.emit(items);
    this.setFormControlValue(items);

    this.isUploading = false;
    this.setVisiblityOfFileUploader(false);
    this.clearFormControlsValues(['manualFile'], this.form);
    this.itemChange.emit();
  }

  private sortStudents(items: AutoCompleteItem[]): void {
    // sort by last name (secondLabel) with fallback first name (firstLabel) if matching
    items.sort((a, b) => {
      let sortValueA;
      let sortValueB;
      if (a.extraLabels.secondLabel === b.extraLabels.secondLabel) {
        sortValueA = a.extraLabels.firstLabel;
        sortValueB = b.extraLabels.firstLabel;
      } else {
        sortValueA = a.extraLabels.secondLabel ?? '';
        sortValueB = b.extraLabels.secondLabel ?? '';
      }

      return sortValueA.toLowerCase().localeCompare(sortValueB.toLowerCase(), 'en', { sensitivity: 'base' });
    });
  }

  private loadGroupSelectDataByUuid(uuid: string): void {
    this.eventManager.broadcast(new EventContent(EventType.PreloaderShow, true));
    this.rosterService.getById(uuid).subscribe(roster => {
      const items: AutoCompleteItem[] = this.userAdapterService.userToAutoCompleteItem(roster.membershipCollection);
      if (this.form?.controls.groupSelectAction.value === this.rosterSelectActions.Add) {
        this.addGroupSelectStudents(items);
      }
      if (this.form?.controls.groupSelectAction.value === this.rosterSelectActions.Remove) {
        this.removeGroupSelectStudents(items);
      }
      this.eventManager.broadcast(new EventContent(EventType.PreloaderShow, false));
    });
  }

  private addGroupSelectStudents(students: AutoCompleteItem[]): void {
    const oldItems: AutoCompleteItem[] = this.form?.controls.items.value === null ? [] : this.form?.controls.items.value;
    students = students.filter((student) => {
      return !oldItems.map((e) => e.value).includes(student.value);
    });
    const items: AutoCompleteItem[] = [...oldItems, ... students];

    this.sortStudents(items);
    this.selectChange.emit(items);
    this.setFormControlValue(items);
  }

  private removeGroupSelectStudents(students: AutoCompleteItem[]): void {
    const oldItems: AutoCompleteItem[] = this.form?.controls.items.value === null ? [] : this.form?.controls.items.value;
    const items: AutoCompleteItem[] = oldItems.filter((student) => {
      return !students.map((e) => e.value).includes(student.value);
    });

    this.sortStudents(items);
    this.selectChange.emit(items);
    this.setFormControlValue(items);
  }
}
